import { LitElement, html, nothing } from "lit";

import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";

import "@phosphor-icons/webcomponents/PhDotsSixVertical";
import { getAssetUrl } from "helpers/assetHelpers";

import { DnDItemController } from "./dnd-item-controller";
import { styles } from "./item-card.css";

export interface ItemCardData {
  id: string;
  title: string;
  subtitle: string;
  imageUrl?: string;
  fileId?: string;
  exclusive?: boolean;
  waiting_list?: boolean;
}

@customElement("item-card")
export class ItemCard extends LitElement {
  static override styles = styles;

  @property({ type: String, reflect: true, attribute: true })
  type: "square" | "rectangle" = "rectangle";

  @property({ type: Object })
  item!: ItemCardData;

  @property({ type: Boolean })
  flourish = false;

  @property({ type: String })
  status?: "success" | "warning" | "error";

  // Create ref for drag handle
  private dragHandleRef = createRef<HTMLElement>();

  // Set up the DnD item controller
  private dndController = new DnDItemController(this, this.item, {
    dragHandle: this.dragHandleRef,
  });

  // Update the controller when item data changes
  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("item")) {
      this.dndController.updateItemData(this.item);
    }
  }

  // Expose dragging state
  get isDragging() {
    return this.dndController.isCurrentlyDragging;
  }

  private renderImage() {
    if (!this.item.imageUrl) {
      return nothing;
    }

    return html`
      <div class="item-card-image">
        <img src=${getAssetUrl(this.item.imageUrl)} alt="${this.item.title || "Item image"}" />
      </div>
    `;
  }

  private renderStatusCircle() {
    if (!this.status) {
      return nothing;
    }

    return html` <div class="item-card-status-circle ${this.status}"></div> `;
  }

  private renderContent() {
    const hasImage = Boolean(this.item.imageUrl);

    return html`
      <div
        data-item-id=${this.item.id}
        class=${classMap({
          "item-card": true,
          "draggable-item": !this.item.exclusive && !this.item.waiting_list,
          "item-card-square": this.type === "square",
          "item-card-rectangle": this.type === "rectangle",
          "flourish-active": this.flourish,
          dragging: this.isDragging && !this.item.exclusive && !this.item.waiting_list,
          "no-select": true,
        })}
      >
        ${this.item.exclusive || this.item.waiting_list
          ? nothing
          : html`
              <div class="item-card-dnd-handle-wrapper">
                <div class="item-card-dnd-handle-hitarea" ${ref(this.dragHandleRef)}></div>
                <div class="item-card-dnd-handle">
                  <slot name="drag-handle">
                    <ph-dots-six-vertical size="20"></ph-dots-six-vertical>
                  </slot>
                </div>
              </div>
            `}

        <div class="content">
          <div class="item-card-header">
            <slot name="actions"></slot>
            ${this.item.exclusive && this.type === "square" ? html`<div class="badge">Exclusive</div>` : nothing}
            ${this.item.waiting_list && this.type === "square" ? html`<div class="badge">Waiting List</div>` : nothing}
          </div>

          ${hasImage && this.type !== "rectangle" ? this.renderImage() : nothing}

          <div class="item-card-title-wrapper">
            ${this.type === "rectangle" && this.item.exclusive ? html`<div class="badge">Exclusive</div>` : nothing}
            ${this.type === "rectangle" && this.item.waiting_list
              ? html`<div class="badge">Waiting List</div>`
              : nothing}
            <div class="item-card-title">
              ${this.renderStatusCircle()}
              <div class="item-card-title-text">${this.item.title}</div>
            </div>

            <div class="item-card-subtitle">${this.item.subtitle}</div>
          </div>

          ${hasImage && this.type === "rectangle" ? this.renderImage() : nothing}
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      ${this.renderContent()}
      <slot name="modals"></slot>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "item-card": ItemCard;
  }
}
